(function () {
    interface EpiWindow extends Window {
        epi?: {
            EPiServer?: {
                Forms?: {
                    Validators?: Record<string, ValidatorObject | ValidateFunction>;
                };
            };
        };
    }

    interface ValidatorMetaData {
        model?: {
            message?: string;
        };
    }

    interface ValidationResult {
        isValid: boolean;
        message: string;
    }

    type ValidateFunction = (
        fieldName: string,
        fieldValue: string,
        validatorMetaData: ValidatorMetaData | null
    ) => ValidationResult | true;

    interface ValidatorObject {
        validate: ValidateFunction;
    }

    const epiWindow = window as EpiWindow;
    epiWindow.epi = epiWindow.epi || {};
    epiWindow.epi.EPiServer = epiWindow.epi.EPiServer || {};
    epiWindow.epi.EPiServer.Forms = epiWindow.epi.EPiServer.Forms || {};
    epiWindow.epi.EPiServer.Forms.Validators = epiWindow.epi.EPiServer.Forms.Validators || {};

    // Safe reference after initialization
    const validators = epiWindow.epi.EPiServer.Forms.Validators;

    const keyPascal = "Securitas.PublicWeb.Web.Models.Elements.EmailValidator";
    const keyLower = keyPascal.toLowerCase();

    const builtInEmailValidator = "EPiServer.Forms.Implementation.Validation.EmailValidator";
    const builtInEmailValidatorLower = builtInEmailValidator.toLowerCase();

    function cleanInvisibleUnicode(email: string): string {
        if (!email) return email;

        return email.replace(/[\u200B-\u200D\uFEFF\u200E\u200F\u202A-\u202E]/g, '').trim();
    }

    function cleanAllEmailInputs() {
        const emailInputs = document.querySelectorAll<HTMLInputElement>(
            'input[type="email"], input.FormTextbox__Input, input.Form__Element'
        );

        emailInputs.forEach((input) => {
            if (input.value) {
                const cleaned = cleanInvisibleUnicode(input.value);
                if (cleaned !== input.value) {
                    input.value = cleaned;
                }
            }
        });
    }

    function setupEmailCleaning() {
        const emailInputs = document.querySelectorAll<HTMLInputElement>(
            'input[type="email"], input.FormTextbox__Input, input.Form__Element'
        );

        emailInputs.forEach((input) => {
            if (input.dataset.emailCleanerInit === 'true') return;
            input.dataset.emailCleanerInit = 'true';

            input.addEventListener('blur', function () {
                if (this.value) {
                    const cleaned = cleanInvisibleUnicode(this.value);
                    if (cleaned !== this.value) {
                        this.value = cleaned;
                        this.dispatchEvent(new Event('change', { bubbles: true }));
                    }
                }
            });

            input.addEventListener('paste', function () {
                setTimeout(() => {
                    if (this.value) {
                        const cleaned = cleanInvisibleUnicode(this.value);
                        if (cleaned !== this.value) {
                            this.value = cleaned;
                            this.dispatchEvent(new Event('input', { bubbles: true }));
                        }
                    }
                }, 10);
            });
        });
    }

    function setupFormSubmitCleaning() {
        const forms = document.querySelectorAll<HTMLFormElement>('.EPiServerForms, form');

        forms.forEach((form) => {
            if (form.dataset.emailSubmitCleanerInit === 'true') return;
            form.dataset.emailSubmitCleanerInit = 'true';

            form.addEventListener('submit', function () {
                cleanAllEmailInputs();
            }, true);

            form.addEventListener('formsStepValidating', function () {
                cleanAllEmailInputs();
            });
        });
    }

    function hasVisibleInvalidCharacters(email: string): boolean {
        if (!email) return false;

        const cleaned = cleanInvisibleUnicode(email);

        return /[^\x20-\x7E]/.test(cleaned);
    }

    function isValidEmail(email: string): boolean {
        if (!email || email.trim() === "") {
            return false;
        }

        email = email.trim();

        // Auto-clean invisible Unicode (don't show error for invisible chars)
        email = cleanInvisibleUnicode(email);

        // Now validate for visible invalid characters (show error for these)
        if (hasVisibleInvalidCharacters(email)) {
            return false;
        }

        // Basic structure validation
        if (!email.includes("@") || email.indexOf("@") !== email.lastIndexOf("@")) {
            return false;
        }

        const parts = email.split("@");
        if (parts.length !== 2) {
            return false;
        }

        const localPart = parts[0];
        const domainPart = parts[1];

        // Validate local part (before @)
        if (!localPart || localPart.length > 64) {
            return false;
        }

        // Check for invalid characters in local part
        if (/[^\w.+-]/.test(localPart)) {
            return false;
        }

        // Local part cannot start or end with a dot
        if (localPart.startsWith(".") || localPart.endsWith(".")) {
            return false;
        }

        // Local part cannot have consecutive dots
        if (localPart.includes("..")) {
            return false;
        }

        // Validate domain part (after @)
        if (!domainPart || domainPart.length > 255) {
            return false;
        }

        // Domain must contain at least one dot
        if (!domainPart.includes(".")) {
            return false;
        }

        // Check for invalid characters in domain (no special characters like �, �, �, etc.)
        if (/[^a-zA-Z0-9.-]/.test(domainPart)) {
            return false;
        }

        // Domain cannot start or end with a dot or hyphen
        if (
            domainPart.startsWith(".") ||
            domainPart.endsWith(".") ||
            domainPart.startsWith("-") ||
            domainPart.endsWith("-")
        ) {
            return false;
        }

        // Domain cannot have consecutive dots
        if (domainPart.includes("..")) {
            return false;
        }

        // Validate TLD (top-level domain)
        const domainSegments = domainPart.split(".");
        const tld = domainSegments[domainSegments.length - 1];

        // TLD must be at least 2 characters and contain only letters
        if (tld.length < 2 || !/^[a-zA-Z]+$/.test(tld)) {
            return false;
        }

        // Each domain segment must not be empty and must not start/end with hyphen
        for (const segment of domainSegments) {
            if (!segment || segment.startsWith("-") || segment.endsWith("-")) {
                return false;
            }
        }

        return true;
    }

    function coreValidate(
        _fieldName: string,
        fieldValue: string,
        validatorMetaData: ValidatorMetaData | null
    ): ValidationResult | true {
        if (!fieldValue || fieldValue.trim() === "") {
            return true;
        }

        const errorMessage =
            validatorMetaData?.model?.message ||
            "Please enter a valid email address.";

        const isValid = isValidEmail(fieldValue);

        if (!isValid) {
            return {
                isValid: false,
                message: errorMessage
            };
        }

        return true;
    }

    const validatorObject: ValidatorObject = { validate: coreValidate };

    validators[keyLower] = validatorObject;
    validators[keyPascal] = validatorObject;
    validators[keyLower + ":fn"] = coreValidate;
    validators[keyPascal + ":fn"] = coreValidate;

    validators[builtInEmailValidatorLower] = validatorObject;
    validators[builtInEmailValidator] = validatorObject;
    validators[builtInEmailValidatorLower + ":fn"] = coreValidate;
    validators[builtInEmailValidator + ":fn"] = coreValidate;

    function initializeCleaning() {
        setupEmailCleaning();
        setupFormSubmitCleaning();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeCleaning);
    } else {
        initializeCleaning();
    }

    document.addEventListener('formsNavigationNextStep', initializeCleaning);
    document.addEventListener('formsNavigationPrevStep', initializeCleaning);
})();